import React, { useState, useEffect } from "react";
import {
  Users,
  Clock,
  Target,
  Filter,
  Download,
  BarChart3,
  PieChart,
  UserPlus,
  UserCheck,
  Briefcase,
  Star,
  FileText,
  RefreshCw,
  IndianRupee,
  ChevronDown,
} from "lucide-react";
import hrAnalyticsService from "../../../api/services/hrAnalyticsService";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import Button from "../../../components/common/Button/Button";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

// CSV Export Function
const exportToCSV = (funnelData, recruitmentMetrics, interviewerPerformance) => {
  const rows = [];

  // Add header row
  rows.push([
    'Recruitment Funnel Report',
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  ]);
  rows.push([]); // Empty row

  // Key Metrics Section
  rows.push(['Key Metrics']);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Applicants', formatIndianNumber(funnelData?.totalApplications || 0)]);
  rows.push(['Conversion Rate', `${(funnelData?.conversionRate || 0).toFixed(1)}%`]);
  rows.push(['Avg Time to Hire', recruitmentMetrics?.timeToHire?.average != null ? `${recruitmentMetrics.timeToHire.average} days` : 'N/A']);
  rows.push([]); // Empty row

  // Recruitment Funnel Section
  if (funnelData?.statusBreakdown) {
    rows.push(['Recruitment Funnel']);
    rows.push(['Stage', 'Count', 'Percentage']);

    const statusBreakdown = funnelData.statusBreakdown;
    const applied = statusBreakdown.applied || 0;
    const screening = statusBreakdown.screening || 0;
    const shortlisted = statusBreakdown.shortlisted || 0;
    const interview = statusBreakdown.interview || 0;
    const interviewed = statusBreakdown.interviewed || 0;
    const offer = statusBreakdown.offer || 0;
    const hired = statusBreakdown.hired || 0;
    const rejected = statusBreakdown.rejected || 0;
    const total = applied + screening + shortlisted + interview + interviewed + offer + hired + rejected;

    const stages = [
      { name: 'Applied', count: applied },
      { name: 'Screening', count: screening },
      { name: 'Shortlisted', count: shortlisted },
      { name: 'Interview Scheduled', count: interview },
      { name: 'Interviewed', count: interviewed },
      { name: 'Offer', count: offer },
      { name: 'Hired', count: hired },
      { name: 'Rejected', count: rejected }
    ];

    stages.forEach((stage) => {
      const percentage = total > 0 ? ((stage.count / total) * 100).toFixed(1) : 0;
      rows.push([
        stage.name,
        formatIndianNumber(stage.count),
        `${percentage}%`
      ]);
    });
    rows.push([]); // Empty row
  }

  // Source Effectiveness Section
  if (recruitmentMetrics?.sourceEffectiveness && recruitmentMetrics.sourceEffectiveness.length > 0) {
    rows.push(['Source Effectiveness']);
    rows.push([
      'Source',
      'Applications',
      'Hired',
      'Rate (%)'
    ]);

    recruitmentMetrics.sourceEffectiveness.forEach((source) => {
      rows.push([
        source.source || '',
        source.applications || 0,
        source.hired || 0,
        `${source.rate || 0}%`
      ]);
    });
    rows.push([]); // Empty row
  }

  // Department Hiring Progress Section
  if (recruitmentMetrics?.departmentHiring && recruitmentMetrics.departmentHiring.length > 0) {
    rows.push(['Department Hiring Progress']);
    rows.push([
      'Department',
      'Target',
      'Hired',
      'Pipeline',
      'Avg Time (days)'
    ]);

    recruitmentMetrics.departmentHiring.forEach((dept) => {
      rows.push([
        dept.department || '',
        dept.target || 0,
        dept.hired || 0,
        dept.pipeline || 0,
        dept.avgTime || 0
      ]);
    });
    rows.push([]); // Empty row
  }

  // Interviewer Performance Section
  if (interviewerPerformance?.data && interviewerPerformance.data.length > 0) {
    rows.push(['Interviewer Performance']);
    rows.push([
      'Interviewer Name',
      'Email',
      'Interviews',
      'Hires',
      'Success Rate (%)'
    ]);

    interviewerPerformance.data.forEach((interviewer) => {
      rows.push([
        interviewer.name || 'Unknown',
        interviewer.email || 'No email',
        interviewer.interviews || 0,
        interviewer.successfulInterviews || 0,
        `${interviewer.successRate || 0}%`
      ]);
    });
  }

  // Convert rows to CSV format
  const csvContent = rows.map(row => {
    return row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const cellValue = String(cell || '');
      if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
        return `"${cellValue.replace(/"/g, '""')}"`;
      }
      return cellValue;
    }).join(',');
  }).join('\n');

  // Create and download CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `Recruitment_Funnel_Report_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const RecruitmentFunnel = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [viewType, setViewType] = useState("funnel");
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState(null);
  const [recruitmentMetrics, setRecruitmentMetrics] = useState(null);
  const [interviewerPerformance, setInterviewerPerformance] = useState(null);

  const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
      let bgColor = "bg-[#0F2942]";
      if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
      else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
      else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
      else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
      else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

      return (
          <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
              <div className="space-y-4 flex-1">
                  <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                      {title}
                  </h3>
                  <div className="flex items-baseline space-x-2">
                      <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                          {value}
                      </span>
                      {(subtitle || trend) && (
                          <div className="flex items-center space-x-2">
                              {subtitle && (
                                  <span className="text-sm font-medium text-slate-500">
                                      {subtitle}
                                  </span>
                              )}
                              {trend && (
                                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                      {trend.label}
                                  </span>
                              )}
                          </div>
                      )}
                  </div>
              </div>
              <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                  <Icon className="w-5 h-5" />
              </div>
          </div>
      );
  };

  // Load data from APIs
  useEffect(() => {
    loadRecruitmentData();
  }, [selectedPeriod, selectedDepartment, dateRange]);

  const loadRecruitmentData = async () => {
    try {
      setLoading(true);

      // Load funnel data with filters
      const funnelResponse = await hrAnalyticsService.getRecruitmentFunnel(
        selectedPeriod,
        selectedDepartment,
        dateRange.startDate,
        dateRange.endDate
      );
      setFunnelData(funnelResponse);

      // Load comprehensive metrics with period filter
      const metricsResponse = await hrAnalyticsService.getRecruitmentMetrics(
        selectedPeriod,
        dateRange.startDate,
        dateRange.endDate
      );
      setRecruitmentMetrics(metricsResponse);

      // Load interviewer performance data with period filter
      const interviewerResponse =
        await hrAnalyticsService.getInterviewerPerformanceStats(
          selectedPeriod,
          dateRange.startDate,
          dateRange.endDate
        );
      setInterviewerPerformance(interviewerResponse);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPerformanceColor = (current, target) => {
    if (current >= target) return "text-green-600";
    if (current >= target * 0.8) return "text-yellow-600";
    return "text-red-600";
  };

  const getConversionColor = (rate) => {
    if (rate >= 25) return "bg-green-500";
    if (rate >= 15) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (loading && !funnelData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading recruitment data...</p>
        </div>
      </div>
    );
  }

  if (!funnelData || !recruitmentMetrics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">No recruitment data available</p>
          <button
            onClick={loadRecruitmentData}
            className="mt-4 px-4 py-2 bg-[#0F2942] text-white rounded-md hover:bg-[#0B2033]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            Recruitment Funnel
          </h2>
          <p className="text-sm text-gray-600">
            Track hiring pipeline performance and recruitment metrics
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <select
              value={selectedPeriod}
              onChange={(e) => {
                const newPeriod = e.target.value;
                setSelectedPeriod(newPeriod);
                if (newPeriod !== "custom") {
                  setDateRange({ startDate: "", endDate: "" });
                }
              }}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:border-blue-500 cursor-pointer"
            >
              <option value="custom">Custom Range</option>
              <option value="current-month">Current Month</option>
              <option value="last-month">Last Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all-data">All Data</option>
            </select>
          </div>

          {selectedPeriod === "custom" && (
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(range) => setDateRange(range)}
              />
            </div>
          )}

          {/* CSV Export */}
          <Button
            variant="outline"
            onClick={() => exportToCSV(
              funnelData,
              recruitmentMetrics,
              interviewerPerformance
            )}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          icon={Users}
          title="Total Applicants"
          value={formatIndianNumber(funnelData.totalApplications || 0)}
          color="bg-[#0F2942]"
        />
        <KpiCard
          icon={Target}
          title="Conversion Rate"
          value={`${funnelData.conversionRate?.toFixed(1) || 0}%`}
          color="bg-emerald-600"
        />
        <KpiCard
          icon={Clock}
          title="Avg Time to Hire"
          value={recruitmentMetrics.timeToHire?.average != null ? `${recruitmentMetrics.timeToHire.average} days` : 'N/A'}
          color="bg-orange-600"
        />
      </div>

      {/* Recruitment Funnel Visualization */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Recruitment Funnel
          </h3>
          <button className="text-gray-400 hover:text-gray-600">
            <BarChart3 className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Applications */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <UserPlus className="h-6 w-6 text-[#0F2942]" />
              <div>
                <p className="font-semibold text-gray-900">Applied</p>
                <p className="text-sm text-gray-600">
                  Initial applications received
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#0F2942]">
                {formatIndianNumber(funnelData?.statusBreakdown?.applied || 0)}
              </p>
            </div>
          </div>

          {/* Screening */}
          <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Filter className="h-6 w-6 text-indigo-600" />
              <div>
                <p className="font-semibold text-gray-900">Screening</p>
                <p className="text-sm text-gray-600">
                  Under initial screening
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.screening || 0)}
              </p>
            </div>
          </div>

          {/* Shortlisted */}
          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Filter className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-semibold text-gray-900">Shortlisted</p>
                <p className="text-sm text-gray-600">
                  Passed initial screening
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.shortlisted || 0)}
              </p>
            </div>
          </div>

          {/* Interview Scheduled */}
          <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Clock className="h-6 w-6 text-pink-600" />
              <div>
                <p className="font-semibold text-gray-900">Interview Scheduled</p>
                <p className="text-sm text-gray-600">Interview scheduled</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-pink-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.interview || 0)}
              </p>
            </div>
          </div>

          {/* Interviewed */}
          <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-cyan-600" />
              <div>
                <p className="font-semibold text-gray-900">Interviewed</p>
                <p className="text-sm text-gray-600">Proceeded to interviews</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-cyan-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.interviewed || 0)}
              </p>
            </div>
          </div>

          {/* Offer */}
          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <UserCheck className="h-6 w-6 text-orange-600" />
              <div>
                <p className="font-semibold text-gray-900">Offer</p>
                <p className="text-sm text-gray-600">Offers extended</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.offer || 0)}
              </p>
            </div>
          </div>

          {/* Hired */}
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <UserCheck className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-semibold text-gray-900">Hired</p>
                <p className="text-sm text-gray-600">Successfully onboarded</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-green-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.hired || 0)}
              </p>
            </div>
          </div>

          {/* Rejected */}
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-gray-900">Rejected</p>
                <p className="text-sm text-gray-600">Candidates rejected</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-red-600">
                {formatIndianNumber(funnelData?.statusBreakdown?.rejected || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Source Effectiveness & Department Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Effectiveness */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Source Effectiveness
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <PieChart className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {recruitmentMetrics.sourceEffectiveness && recruitmentMetrics.sourceEffectiveness.length > 0 ? (
              recruitmentMetrics.sourceEffectiveness.map((source, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {source.source}
                    </p>
                    <p className="text-xs text-gray-600">
                      {source.applications} applications
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {source.hired} Hired
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-600">
                        {source.rate}%
                      </span>
                      <div
                        className={`w-2 h-2 rounded-full ${getConversionColor(
                          parseFloat(source.rate)
                        )}`}
                      ></div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No source effectiveness data found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No recruitment source data is available for the selected period.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Department Hiring Progress */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Department Hiring Progress
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Briefcase className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {recruitmentMetrics.departmentHiring && recruitmentMetrics.departmentHiring.length > 0 ? (
              recruitmentMetrics.departmentHiring.map((dept, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[60%]" title={dept.department}>
                      {dept.department}
                    </span>
                    <span className="text-sm text-gray-600 flex-shrink-0 ml-2">
                      {dept.hired}/{dept.target} Hired
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${dept.hired >= dept.target
                        ? "bg-green-500"
                        : dept.hired >= dept.target * 0.8
                          ? "bg-yellow-500"
                          : "bg-red-500"
                        }`}
                      style={{
                        width: `${Math.min(
                          (dept.hired / dept.target) * 100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{dept.avgTime} days avg</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No department hiring data found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No department hiring progress data is available for the selected period.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interviewer Performance */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Interviewer Performance
          </h3>
          <button className="text-gray-400 hover:text-gray-600">
            <Star className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Interviewer
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Interviews
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Hires
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Success Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {interviewerPerformance?.data && interviewerPerformance.data.length > 0 ? (
                interviewerPerformance.data.map((interviewer, index) => {
                  return (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4 max-w-[200px]">
                        <p className="text-sm font-medium text-gray-900 truncate" title={interviewer.name || "Unknown"}>
                          {interviewer.name || "Unknown"}
                        </p>
                        <p className="text-xs text-gray-500 truncate" title={interviewer.email || "No email"}>
                          {interviewer.email || "No email"}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-600">
                          {formatIndianNumber(interviewer.interviews)}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-600">
                          {formatIndianNumber(interviewer.successfulInterviews)}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-12 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getConversionColor(
                                interviewer.successRate
                              )}`}
                              style={{
                                width: `${Math.min(
                                  interviewer.successRate,
                                  100
                                )}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {interviewer.successRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" className="py-12 px-4">
                    <div className="text-center">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No interviewer performance data found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No interviewer performance data is available for the selected period.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentFunnel;
